import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useLottery } from "../../hooks/useLottery";
import { useLotteries } from "../../hooks/useLotteries";
import ChristmasLoader from "../../Components/ChristmasLoader";
import { StatusChip } from "../../Components/StatusChip";

export const LotterySessions = () => {
  const { lotteries, loading } = useLotteries();
  const { deleteLottery } = useLottery(undefined);

  const handleDelete = async (sessionId: string) => {
    const confirmed = window.confirm(
      "Είσαι σίγουρος ότι θέλεις να διαγράψεις αυτή τη λοταρία;"
    );
    if (!confirmed) return;
    await deleteLottery(sessionId);
    window.location.reload();
  };

  if (loading) {
    return <ChristmasLoader size={240} sparkle={true} />;
  }
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(30, 50%, 98%)", py: 4 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Mountains of Christmas", cursive',
              color: "hsl(150, 60%, 25%)",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            🎄 ΛΟΤΑΡΙΕΣ
          </Typography>
          <Button
            component={Link}
            to="/lotteries/new"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              background:
                "linear-gradient(135deg, hsl(350, 80%, 40%) 0%, hsl(350, 85%, 30%) 100%)",
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              "&:hover": {
                background:
                  "linear-gradient(135deg, hsl(350, 75%, 45%) 0%, hsl(350, 80%, 35%) 100%)",
              },
            }}
          >
            ΝΕΑ ΛΟΤΑΡΙΑ
          </Button>
        </Box>

        {lotteries.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 10,
              bgcolor: "white",
              borderRadius: 4,
              border: "2px dashed hsl(30, 20%, 85%)",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Mountains of Christmas", cursive',
                color: "hsl(150, 60%, 25%)",
                mb: 2,
              }}
            >
              Δεν υπάρχουν διαθέσιμες λοταρίες
            </Typography>
            <Typography sx={{ color: "hsl(150, 20%, 35%)", mb: 3 }}>
              Δημιούργησε την πρώτη σου Χριστουγεννιάτικη λοταρία για να
              ξεκινήσεις!
            </Typography>
            <Button
              component={Link}
              to="/lotteries/new"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                background:
                  "linear-gradient(135deg, hsl(350, 80%, 40%) 0%, hsl(350, 85%, 30%) 100%)",
                color: "white",
              }}
            >
              Δημιουργια Λοταριας
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {lotteries.map((lottery) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={lottery.sessionId}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: "2px solid hsl(30, 20%, 85%)",
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 40px -12px rgba(0,0,0,0.15)",
                      borderColor: "hsl(350, 80%, 40%)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: '"Mountains of Christmas", cursive',
                          color: "hsl(150, 60%, 25%)",
                        }}
                      >
                        {lottery.title}
                      </Typography>
                      <StatusChip status={lottery.status} />
                    </Box>
                    <Typography sx={{ color: "hsl(150, 20%, 35%)", mb: 2 }}>
                      {lottery.description}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 18, color: "hsl(350, 80%, 40%)" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "hsl(150, 20%, 35%)" }}
                        >
                          {new Date(lottery.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PeopleIcon
                          sx={{ fontSize: 18, color: "hsl(150, 60%, 25%)" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "hsl(150, 20%, 35%)" }}
                        >
                          {lottery.totalTickets} Λαχνοί
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      component={Link}
                      to={`/lotteries/${lottery.sessionId}/edit`}
                      size="small"
                      startIcon={<EditIcon />}
                      sx={{ color: "hsl(150, 60%, 25%)" }}
                    >
                      Επεξεργασια
                    </Button>
                    <Button
                      component={Link}
                      startIcon={<CardGiftcardIcon />}
                      to={`/prizes`}
                      size="small"
                      sx={{ color: "hsl(45, 90%, 35%)" }}
                    >
                      Δωρα
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      sx={{ ml: "auto", color: "hsl(0, 60%, 50%)" }}
                      onClick={() => {
                        if (!lottery.sessionId) return;
                        handleDelete(lottery.sessionId);
                      }}
                    >
                      Διαγραφη
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};
